import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { PatFormData } from 'components/Assessments/PatAssessment/types/PatFormData'
import { PatAssessmentQuestionnaireItem } from 'components/Assessments/PatAssessment/types/questionnaire'
import { CodingFieldAdapter } from 'components/Assessments/PatAssessment/adapters/CodingFieldAdapter'
import { ModifierBFormField } from './formField'

/**
 * Modifier B subsection adapter for PAT Assessment
 * Handles modifier B extraction and transformation
 */
export class ModifierBAdapter extends CodingFieldAdapter {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<PatFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Extract coding values for questions using helper method
    const question1 = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_B_1,
      false
    )

    const question2 = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_B_2,
      false
    )

    const question3 = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_B_3,
      false
    )

    const question4 = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_B_4,
      false
    )

    const question5 = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_B_5,
      false
    )

    const question6 = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_B_6,
      false
    )

    const question7 = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_B_7,
      false
    )

    // Extract notes fields using helper method
    const notes1 = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_B_1_NOTES
    )

    const notes2 = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_B_2_NOTES
    )

    const notes3 = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_B_3_NOTES
    )

    const notes4 = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_B_4_NOTES
    )

    const notes5 = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_B_5_NOTES
    )

    const notes6 = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_B_6_NOTES
    )

    const notes7 = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_B_7_NOTES
    )

    return {
      [ModifierBFormField.MODIFIER_B_1_QUESTION]: question1,
      [ModifierBFormField.MODIFIER_B_1_NOTES]: notes1,
      [ModifierBFormField.MODIFIER_B_2_QUESTION]: question2,
      [ModifierBFormField.MODIFIER_B_2_NOTES]: notes2,
      [ModifierBFormField.MODIFIER_B_3_QUESTION]: question3,
      [ModifierBFormField.MODIFIER_B_3_NOTES]: notes3,
      [ModifierBFormField.MODIFIER_B_4_QUESTION]: question4,
      [ModifierBFormField.MODIFIER_B_4_NOTES]: notes4,
      [ModifierBFormField.MODIFIER_B_5_QUESTION]: question5,
      [ModifierBFormField.MODIFIER_B_5_NOTES]: notes5,
      [ModifierBFormField.MODIFIER_B_6_QUESTION]: question6,
      [ModifierBFormField.MODIFIER_B_6_NOTES]: notes6,
      [ModifierBFormField.MODIFIER_B_7_QUESTION]: question7,
      [ModifierBFormField.MODIFIER_B_7_NOTES]: notes7
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    // Build coding value mutations using helper method
    const question1Mutation = this.buildSingleCodingValueMutation(
      ModifierBFormField.MODIFIER_B_1_QUESTION,
      PatAssessmentQuestionnaireItem.MODIFIER_B_1,
      false
    )
    if (question1Mutation) {
      mutations.push(question1Mutation)
    }

    const question2Mutation = this.buildSingleCodingValueMutation(
      ModifierBFormField.MODIFIER_B_2_QUESTION,
      PatAssessmentQuestionnaireItem.MODIFIER_B_2,
      false
    )
    if (question2Mutation) {
      mutations.push(question2Mutation)
    }

    const question3Mutation = this.buildSingleCodingValueMutation(
      ModifierBFormField.MODIFIER_B_3_QUESTION,
      PatAssessmentQuestionnaireItem.MODIFIER_B_3,
      false
    )
    if (question3Mutation) {
      mutations.push(question3Mutation)
    }

    const question4Mutation = this.buildSingleCodingValueMutation(
      ModifierBFormField.MODIFIER_B_4_QUESTION,
      PatAssessmentQuestionnaireItem.MODIFIER_B_4,
      false
    )
    if (question4Mutation) {
      mutations.push(question4Mutation)
    }

    const question5Mutation = this.buildSingleCodingValueMutation(
      ModifierBFormField.MODIFIER_B_5_QUESTION,
      PatAssessmentQuestionnaireItem.MODIFIER_B_5,
      false
    )
    if (question5Mutation) {
      mutations.push(question5Mutation)
    }

    const question6Mutation = this.buildSingleCodingValueMutation(
      ModifierBFormField.MODIFIER_B_6_QUESTION,
      PatAssessmentQuestionnaireItem.MODIFIER_B_6,
      false
    )
    if (question6Mutation) {
      mutations.push(question6Mutation)
    }

    const question7Mutation = this.buildSingleCodingValueMutation(
      ModifierBFormField.MODIFIER_B_7_QUESTION,
      PatAssessmentQuestionnaireItem.MODIFIER_B_7,
      false
    )
    if (question7Mutation) {
      mutations.push(question7Mutation)
    }

    // Build notes field mutations using helper method
    const notes1Mutation = this.buildNotesFieldMutation(
      ModifierBFormField.MODIFIER_B_1_NOTES,
      PatAssessmentQuestionnaireItem.MODIFIER_B_1_NOTES
    )
    if (notes1Mutation) {
      mutations.push(notes1Mutation)
    }

    const notes2Mutation = this.buildNotesFieldMutation(
      ModifierBFormField.MODIFIER_B_2_NOTES,
      PatAssessmentQuestionnaireItem.MODIFIER_B_2_NOTES
    )
    if (notes2Mutation) {
      mutations.push(notes2Mutation)
    }

    const notes3Mutation = this.buildNotesFieldMutation(
      ModifierBFormField.MODIFIER_B_3_NOTES,
      PatAssessmentQuestionnaireItem.MODIFIER_B_3_NOTES
    )
    if (notes3Mutation) {
      mutations.push(notes3Mutation)
    }

    const notes4Mutation = this.buildNotesFieldMutation(
      ModifierBFormField.MODIFIER_B_4_NOTES,
      PatAssessmentQuestionnaireItem.MODIFIER_B_4_NOTES
    )
    if (notes4Mutation) {
      mutations.push(notes4Mutation)
    }

    const notes5Mutation = this.buildNotesFieldMutation(
      ModifierBFormField.MODIFIER_B_5_NOTES,
      PatAssessmentQuestionnaireItem.MODIFIER_B_5_NOTES
    )
    if (notes5Mutation) {
      mutations.push(notes5Mutation)
    }

    const notes6Mutation = this.buildNotesFieldMutation(
      ModifierBFormField.MODIFIER_B_6_NOTES,
      PatAssessmentQuestionnaireItem.MODIFIER_B_6_NOTES
    )
    if (notes6Mutation) {
      mutations.push(notes6Mutation)
    }

    const notes7Mutation = this.buildNotesFieldMutation(
      ModifierBFormField.MODIFIER_B_7_NOTES,
      PatAssessmentQuestionnaireItem.MODIFIER_B_7_NOTES
    )
    if (notes7Mutation) {
      mutations.push(notes7Mutation)
    }

    return mutations
  }
}
